import React, { useState, useEffect } from 'react';
import styles from './InstallPrompt.module.css';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if already running as installed app
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    if (isInstalled) {
      return;
    }

    // Check if user has dismissed the prompt and if it's still valid
    const dismissedUntil = localStorage.getItem('pwa-install-dismissed');
    if (dismissedUntil) {
      const expiryDate = new Date(dismissedUntil);
      if (expiryDate > new Date()) {
        // Still within dismissal period
        return;
      } else {
        // Dismissal period expired, remove it
        localStorage.removeItem('pwa-install-dismissed');
      }
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show the install prompt
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if already installed
    window.addEventListener('appinstalled', () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.removeItem('pwa-install-dismissed');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Remember that user dismissed it (for 30 days)
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30);
    localStorage.setItem('pwa-install-dismissed', expiryDate.toISOString());
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className={styles.installPromptOverlay}>
      <div className={styles.installPrompt}>
        <button 
          className={styles.closeButton} 
          onClick={handleDismiss}
          aria-label="Close install prompt"
        >
          ×
        </button>
        
        <div className={styles.content}>
          <div className={styles.icon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          
          <div className={styles.textContent}>
            <h2>Install Prism LIMS</h2>
            <p>
              Install the Prism app for streamlined experience.
            </p>
          </div>
          
          <div className={styles.actions}>
            <button 
              className={styles.installButton} 
              onClick={handleInstallClick}
            >
              Install App
            </button>
            <button 
              className={styles.dismissButton} 
              onClick={handleDismiss}
            >
              Not Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
